import { ProCard } from "@ant-design/pro-components";
import { Card, Flex, Spin } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import ConversationContent from "../ConversationContent";
import FooterDrawer from "../FooterDrawer";
import { useI18n } from "../../contexts/I18nContext";
import { getAgentList, openSession } from "../../services";
import dayjs from "dayjs";
import RenderExtraContent from "../RenderExtraContent";
import {
  getAgentFromScript,
  getSiteTheme,
  siteServiceLogo,
  getTargetUrl,
} from "../../utils";
import { ErrorShowType, isNetworkAvailable } from "../../utils/request";
import { roleType } from "../../constants";
import FooterLinks from "../FooterLinks";
import toast from "react-hot-toast";
import ToasterComponents from "../ToasterComponents";
import MessageInput from "../MessageInput";
import "./index.scss";

const DialogueCard = () => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const footerDrawerRef = useRef<any>(null);
  const contentRef = useRef<any>(null);
  const dialogueContentRef = useRef<any>(null);
  // const { isConnected, setMessages, closeConnection } = useWebSocket();
  const { formatMessage, setLocale } = useI18n();
  const [dialogueMessages, setDialogueMessages] = useState<any[]>([]);
  const [agentData, setAgentData] = useState<any>({}); //智能体对象
  const [isCloseSession, setIsCloseSession] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [svgLoading, setSvgLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const sseSourceRef = useRef<EventSource | null>(null); //用户存储SSE源的ref
  //获取当前接口地址
  // const baseUrl =
  //   process.env.REACT_APP_ENV === "production"
  //     ? process.env.REACT_APP_BASE_URL
  //     : "https://ai-agent-test-api.afirstsoft.cn";
  const baseUrl = getTargetUrl();

  const initOpenSession = async () => {
    await openSession({ agent: getAgentFromScript() });
  };

  const handleCloseCard = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    if (openModal && isCloseSession) {
      initOpenSession();
    }
  }, [openModal, isCloseSession]);

  // 创建关闭 SSE 连接的方法
  const closeSSE = useCallback(() => {
    if (sseSourceRef.current) {
      sseSourceRef.current.close();
      sseSourceRef.current = null;
      setFetchLoading(false);
    }
  }, []);

  // 组件卸载时自动关闭 SSE
  useEffect(() => {
    return () => {
      closeSSE();
    };
  }, [closeSSE]);

  //获取智能体初始化列表
  const getAgentInitList = async () => {
    try {
      const { data } = await getAgentList({
        agent: getAgentFromScript(),
      });
      setLocale(data?.setting?.lang);
      setAgentData(data);
      setDialogueMessages(data?.messages);
    } catch (error) {
      console.error(error);
    }
  };

  // useEffect(() => {
  //   console.log(isConnected, "是否连接成功");
  // }, []);

  // 滚动到底部的函数
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      const dialogueContentEle = dialogueContentRef?.current;
      const contentEle = contentRef?.current;
      if (contentEle && dialogueContentEle) {
        const height = dialogueContentEle.scrollHeight + 74;
        contentEle.scrollTo({
          top: height,
          left: 0,
          behavior: "smooth",
        });
      }
    }, 0);
  }, []);

  //发送消息
  const handleSendMessage = async (values: { content: string }, form: any) => {
    const { content } = values;
    // 检查网络状态
    if (!isNetworkAvailable) {
      toast.error(formatMessage("networkAnomalyText"));
      return;
    }

    // 检查是否已有请求在进行中
    if (fetchLoading) {
      toast.loading(formatMessage("loadingAnswerQuestions"));
      return;
    }
    // 添加用户消息
    const userMessage = {
      role: roleType.user,
      message: content,
      create_time: dayjs().valueOf() / 1000,
    };
    //初始化ai消息
    const initMessage = {
      role: roleType.assistant,
      message: "",
      create_time: dayjs().valueOf() / 1000,
      loading: true,
      isTemp: true,
    };
    setDialogueMessages((prev) => [...prev, userMessage, initMessage]);
    //清空表单
    form?.resetFields();
    //滚动到底部
    scrollToBottom();

    //调接口, 处理接口返回的流数据
    setFetchLoading(true);
    const params = new URLSearchParams({
      agent: getAgentFromScript(),
      question: content,
    }).toString();
    let source = new EventSource(`${baseUrl}/api/v1/agent/converse?${params}`, {
      withCredentials: true,
    });
    sseSourceRef.current = source;
    source?.addEventListener("message", (event) => {
      let res = JSON.parse(event.data);
      if (res?.code === 0) {
        setDialogueMessages((prev) => {
          const newMessages = [...prev];
          const lastIndex = newMessages.length - 1;
          res.data?.messages.forEach((item: any, index: number) => {
            if (index === 0) {
              // 第一条消息：更新初始AI消息
              newMessages[lastIndex] = {
                ...item,
                loading: false,
              };
            } else {
              // 后续消息：添加到新消息数组
              newMessages.splice(lastIndex + index, 0, {
                ...item,
                loading: false,
              });
            }
          });
          return newMessages;
        });
        // 每次更新后滚动到底部
        scrollToBottom();
      } else {
        if (
          [
            ErrorShowType.SILENT,
            ErrorShowType.WARN_MESSAGE,
            ErrorShowType.USERIPCHANGES,
          ].includes(res?.code)
        ) {
          toast.error(res.err_msg);
          //重新初始化智能体
          getAgentInitList();
        } else if (res?.code === 400) {
          //参数校验错误
          toast(formatMessage("systemErrorText"));
          setDialogueMessages((prev) =>
            prev.filter((msg) => msg !== initMessage)
          );
        } else {
          toast.error(res.err_msg);
          //移除初始化信息
          setDialogueMessages((prev) =>
            prev.filter((msg) => msg !== initMessage)
          );
        }
      }
    });
    source.addEventListener("end", () => {
      closeSSE();
      //完成时移除加载状态
      setDialogueMessages((prev) => {
        return prev.map((item) =>
          item.loading ? { ...item, loading: false } : item
        );
      });
      console.log("流式传输完成");
    });
    source.addEventListener("error", () => {
      closeSSE();
      //完成时移除加载状态
      setDialogueMessages((prev) => {
        return prev.map((item) =>
          item.loading ? { ...item, loading: false } : item
        );
      });
    });
  };

  return (
    <>
      {openModal ? (
        <div id="ai-serve-modal">
          <ProCard
            ref={cardRef}
            bordered
            style={{
              width: agentData?.setting?.width
                ? `${agentData?.setting?.width}`
                : 400,
              height: agentData?.setting?.height
                ? `${agentData?.setting?.height}`
                : 500,
            }}
            headerBordered
            className="ai-server-card"
            title={
              <Flex gap={8}>
                {agentData?.setting?.logo && (
                  <img width={18} src={agentData?.setting?.logo} alt="" />
                )}
                <span>{agentData?.setting?.brand_name}</span>
              </Flex>
            }
            actions={
              <FooterLinks
                settingObj={agentData?.setting}
                footerDrawerRef={footerDrawerRef}
              />
            }
            extra={
              <RenderExtraContent
                handleCloseCard={handleCloseCard}
                cardRef={cardRef}
                agentData={agentData}
                setIsCloseSession={setIsCloseSession}
                setAgentData={setAgentData}
                setDialogueMessages={setDialogueMessages}
                closeSSE={closeSSE}
              />
            }
          >
            <Flex
              vertical={true}
              justify="space-between"
              style={{ height: "100%" }}
            >
              <div className="content" ref={contentRef}>
                <ConversationContent
                  messages={dialogueMessages}
                  dialogueContentRef={dialogueContentRef}
                />
              </div>
              <Card.Grid className="input-content">
                <MessageInput handleSendMessage={handleSendMessage} />
              </Card.Grid>
            </Flex>
          </ProCard>
          {/* 底部抽屉 */}
          <FooterDrawer
            ref={footerDrawerRef}
            cardRef={cardRef}
            linkList={agentData?.setting?.links}
          />
          {/* 需要添加totas容器来显示totast */}
          <ToasterComponents />
        </div>
      ) : (
        <div
          className="wake-up-img-logo"
          onClick={async () => {
            setSvgLoading(true);
            //初始化智能体
            await getAgentInitList();
            setSvgLoading(false);
            setOpenModal(true);
          }}
        >
          <Spin size="small" spinning={svgLoading}>
            <img
              width={50}
              src={
                process.env.REACT_APP_AGENT_URL +
                siteServiceLogo[getSiteTheme(window.location.host)].logo
              }
              alt=""
            />
          </Spin>
        </div>
      )}
    </>
  );
};

export default DialogueCard;
